import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { MedicalAllergyObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type MedicalAllergyStoreAction =
  | { type: "SET_MEDICAL_ALLERGY"; payload: MedicalAllergyObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_MEDICAL_ALLERGY"; payload: MedicalAllergyObj }
  | { type: "DELETE_MEDICAL_ALLERGY"; payload: string }
  | { type: "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_MEDICAL_ALLERGY_SAVING_NEW"; payload: boolean };

// Reducer Function
const medicalAllergyStoreReducer = (
  state: MedicalAllergyStoreState,
  action: MedicalAllergyStoreAction
): MedicalAllergyStoreState => {
  switch (action.type) {
    case "SET_MEDICAL_ALLERGY":
      return {
        ...state,
        medicalAllergy: action.payload,
        medicalAllergyLoading: false,
      };

    case "SET_LOADING":
      return { ...state, medicalAllergyLoading: action.payload };

    case "ADD_OR_UPDATE_MEDICAL_ALLERGY":
      const index = state.medicalAllergy.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedMedicalAllergy = [...state.medicalAllergy];
      if (index > -1) {
        updatedMedicalAllergy[index] = action.payload;
      } else {
        updatedMedicalAllergy.push(action.payload);
      }
      return { ...state, medicalAllergy: updatedMedicalAllergy };

    case "DELETE_MEDICAL_ALLERGY":
      return {
        ...state,
        medicalAllergy: state.medicalAllergy.filter(
          (l) => l.id !== action.payload
        ),
      };
    case "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD":
      return { ...state, medicalAllergiesFilestoUpload: action.payload };
    case "SET_MEDICAL_ALLERGY_SAVING_NEW":
      return { ...state, medicalAllergySavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface MedicalAllergyStoreState {
  medicalAllergy: MedicalAllergyObj[];
  medicalAllergyLoading: boolean;
  medicalAllergySavingNew: boolean;
  medicalAllergiesFilestoUpload: File[];
}

// Hook
export const useMedicalAllergyStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(medicalAllergyStoreReducer, {
    medicalAllergy: [],
    medicalAllergyLoading: true,
    medicalAllergiesFilestoUpload: [],
    medicalAllergySavingNew: false,
  });

  const deleteMedicalAllergyById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchMedicalAllergyData = useV3GenericGetData<MedicalAllergyObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createMedicalAllergy = useV3Create<MedicalAllergyObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateMedicalAllergy = useV3Update<MedicalAllergyObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<MedicalAllergyObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleMedicalAllergyFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.medicalAllergiesFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "allergies",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_MEDICAL_ALLERGY",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.medicalAllergiesFilestoUpload, ud_id, uploadDocument]
  );

  const handleDownloadMedicalAllergyDocument = async (
    medicalAllergy_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !medicalAllergy_id || !ud_id) {
      return Promise.reject("Missing required parameters");
    }
    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "allergies",
      entity_id: medicalAllergy_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in medicalAllergy objects' documents array
          downloadFileFromS3(
            res,
            state.medicalAllergy
              ?.find((p) => p.id === medicalAllergy_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeleteMedicalAllergyDocument = useCallback(
    async (
      medicalAllergy_id: string,
      document_id: string | null
    ): Promise<void> => {
      if (!document_id || !medicalAllergy_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "allergies",
          entity_id: medicalAllergy_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the medicalAllergy object
          const updatedMedicalAllergy = state.medicalAllergy.map((p) => {
            if (p.id === medicalAllergy_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_MEDICAL_ALLERGY",
            payload: updatedMedicalAllergy,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.medicalAllergy, deleteDocumentById, ud_id]
  );

  const handleMedicalAllergyFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD",
        payload: [...state.medicalAllergiesFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.medicalAllergiesFilestoUpload]
  );

  const handleMedicalAllergyFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.medicalAllergiesFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.medicalAllergiesFilestoUpload]
  );

  const handleMedicalAllergyFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_MEDICAL_ALLERGY_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchMedicalAllergyItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchMedicalAllergyData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "allergies",
        });

        if (isMounted()) {
          dispatch({ type: "SET_MEDICAL_ALLERGY", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchMedicalAllergyData, isMounted]
  );

  const handleAddOrUpdateMedicalAllergySingleCall = useCallback(
    async (
      medicalAllergyItem: MedicalAllergyObj
    ): Promise<MedicalAllergyObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(medicalAllergyItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateMedicalAllergy(
            {
              ud_id: ud_id,
              entity: "allergies",
              entity_id: medicalAllergyItem.id || undefined,
            },
            medicalAllergyItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createMedicalAllergy(
            { ud_id: ud_id, entity: "allergies" }, // Adjust according to your actual API parameter requirements
            medicalAllergyItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_MEDICAL_ALLERGY", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save Medical Allergy item.");
        console.error(error);
        return null;
      }
    },
    [ud_id, createMedicalAllergy, updateMedicalAllergy]
  );

  const handleAddOrUpdateMedicalAllergy = useCallback(
    async (medicalAllergyItem: MedicalAllergyObj) => {
      dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: true });
      if (state.medicalAllergiesFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateMedicalAllergySingleCall(
            medicalAllergyItem
          );
          if (res) {
            await handleMedicalAllergyFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Allergy data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateMedicalAllergySingleCall(medicalAllergyItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit Medical Allergy data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateMedicalAllergySingleCall,
      state.medicalAllergiesFilestoUpload,
      handleMedicalAllergyFilesUpload,
    ]
  );

  const handleDeleteMedicalAllergy = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteMedicalAllergyById({
          ud_id: ud_id,
          entity: "allergies",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_MEDICAL_ALLERGY", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_MEDICAL_ALLERGY_SAVING_NEW", payload: false });
      }
    },
    [deleteMedicalAllergyById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchMedicalAllergyItems(ud_id); // Fetch all medicalAllergy items
    }
  }, [ fetchMedicalAllergyItems, ud_id]);

  return {
    medicalAllergy: state.medicalAllergy,
    medicalAllergyLoading: state.medicalAllergyLoading,
    medicalAllergySavingNew: state.medicalAllergySavingNew,
    medicalAllergiesFilestoUpload: state.medicalAllergiesFilestoUpload,
    handleAddOrUpdateMedicalAllergy,
    handleDeleteMedicalAllergy,
    handleMedicalAllergyFileRemove,
    handleMedicalAllergyFilesDrop,
    handleMedicalAllergyFilesRemoveAll,
    handleDownloadMedicalAllergyDocument,
    handleDeleteMedicalAllergyDocument,
  };
};
